import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import BASE_URL from "../../../config/apiConfig";
import "./CropStages.scss";
import { format } from "date-fns"; // Import the format function from date-fns for date formatting
import { Autocomplete } from "@mui/material";

function CropStages() {
  const [data, setData] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false); // for delete confirmation
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false); // loading state for delete
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [productsList, setProductsList] = useState([]);

  const [formData, setFormData] = useState({
    crop_name: "",
    crop_stage: "",
    starting_date: "",
    ending_date: "",
    disease: "",
    pests: "",
    temperature_range: "",
    humidity_percentage: "",
    sky: "",
    products: [], // Initialize as an empty array
  });

  useEffect(() => {
    const fetchCropStages = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/crop-stages/crops`
        );
        // Ensure each row has a unique id, fallback to index if id is missing
        const processedData = response.data.map((item, index) => ({
          ...item,
          id: item.id || index, // Ensure each row has a unique id
        }));
        setData(processedData);

      } catch (error) {
        handleToast("Error fetching crop data", "error");
      } finally {
        setLoading(false);
      }
    };
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/admin/products`);
        setProductsList(response.data); // Assuming the response contains an array of products
      } catch (error) {
        handleToast("Error fetching products", "error");
      }
    };
    fetchCropStages();
    fetchProducts();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProductSelection = (event, value) => {
    // Extract only the IDs of the selected products (as integers)
    const productIds = value.map((product) => product.id);
    setFormData({ ...formData, products: productIds }); // Store only the product IDs (array of integers)
  };

  const validateFormData = () => {
    const {
      crop_name,
      crop_stage,
      starting_date,
      ending_date,
      temperature_range,
      humidity_percentage,
    } = formData;

    if (!crop_name.trim()) {
      handleToast("Crop name is required", "error");
      return false;
    }
    if (!crop_stage.trim()) {
      handleToast("Crop stage is required", "error");
      return false;
    }
    if (isEditModalOpen) {
      // Skip date validation if not modified in edit mode
      if (
        (!starting_date || !ending_date) &&
        (!selectedRow?.starting_date || !selectedRow?.ending_date)
      ) {
        handleToast("Both starting and ending dates are required", "error");
        return false;
      }
    } else {
      if (!starting_date || !ending_date) {
        handleToast("Both starting and ending dates are required", "error");
        return false;
      }
      if (new Date(starting_date) > new Date(ending_date)) {
        handleToast("Starting date cannot be after ending date", "error");
        return false;
      }
    }

    // Humidity validation: Ensure the format is like >70 or <70 or =70
    if (!/^[<>]?\d+(\.\d+)?$/.test(humidity_percentage)) {
      handleToast(
        "Humidity percentage must be in the format '>70', '<70', or '=70'",
        "error"
      );
      return false;
    }

    if (!/^\d+(\.\d+)?-\d+(\.\d+)?$/.test(temperature_range)) {
      handleToast(
        "Temperature range must be in format 'min-max' with numeric values",
        "error"
      );
      return false;
    }

    return true;
  };

  const handleAddNew = async (e) => {
    e.preventDefault();
    if (!validateFormData()) return;

    try {
      const response = await axios.post(
        `${BASE_URL}/api/admin/crop-stages/create-crop-stage`,
        formData
      );
      setData((prevData) => [...prevData, response.data]);
      handleToast("Crop added successfully", "success");
    } catch (error) {
      handleToast(error.response?.data?.error || "Failed to add crop", "error");
    } finally {
      setIsAddModalOpen(false);
      resetFormData();
    }
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setFormData({
      ...row,
      products: row.products || [], // Ensure products is an array
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateFormData()) return;

    try {
      const updatedData = { ...formData, id: selectedRow.id };

      // Ensure id is intact
      const response = await axios.put(
        `${BASE_URL}/api/admin/crop-stages/updateCropHealthStage/${selectedRow.id}`,
        updatedData
      );

      const updateResponse = {
        data: {
          id: selectedRow.id,
          ...updatedData,
        },
      };

      setData((prevData) =>
        prevData.map((item) =>
          item.id === selectedRow.id ? updateResponse.data : item
        )
      );

      handleToast("Crop updated successfully", "success");
    } catch (error) {
      handleToast(
        error.response?.data?.error || "Failed to update crop",
        "error"
      );
    } finally {
      setIsEditModalOpen(false);
      resetFormData();
    }
  };

  const handleDelete = async () => {
    setDeleting(true); // Set loading state to true when deleting starts
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/crop-stages/deleteCropHealthStage/${selectedRow.id}`
      );
      setData((prevData) =>
        prevData.filter((item) => item.id !== selectedRow.id)
      );
      handleToast("Crop deleted successfully", "success");
    } catch (error) {
      handleToast(
        error.response?.data?.error || "Failed to delete crop",
        "error"
      );
    } finally {
      setIsDeleteConfirmationOpen(false); // Close the delete confirmation dialog
      setDeleting(false); // Reset the loading state
    }
  };

  const handleDeleteConfirmationOpen = (row) => {
    setSelectedRow(row);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedRow(null);
  };

  const handleToast = (message, severity) => {
    setToast({ open: true, message, severity });
  };

  const closeToast = () => {
    setToast({ open: false, message: "", severity: "success" });
  };

  const resetFormData = () => {
    setFormData({
      crop_name: "",
      crop_stage: "",
      starting_date: "",
      ending_date: "",
      disease: "",
      pests: "",
      temperature_range: "",
      humidity_percentage: "",
      sky: "",
      products: [], // Reset to an empty array
    });
    setSelectedRow(null);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "crop_name", headerName: "Crop Name", width: 150 },
    { field: "crop_stage", headerName: "Crop Stage", width: 150 },
    {
      field: "starting_date",
      headerName: "Starting Date",
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "dd MMM"); // Format the date to "03 Jan"
      },
    },
    {
      field: "ending_date",
      headerName: "Ending Date",
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "dd MMM");
      },
    },
    { field: "humidity_percentage", headerName: "Humidity (%)", width: 150 },
    { field: "temperature_range", headerName: "Temperature Range", width: 200 },
    { field: "sky", headerName: "Sky", width: 120 },
    { field: "disease", headerName: "Disease", width: 120 },
    { field: "pests", headerName: "Pests", width: 120 },
    { field: "products", headerName: "Products", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => handleDeleteConfirmationOpen(params.row)}
            >
              Delete
            </Button>
          </Tooltip>
        </>
      ),
      width: 200,
    },
  ];

  return (
    <div className="cropStages">
      <div className="header">
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAddModalOpen(true)}
        >
          Add New Crop
        </Button>
      </div>
      {loading ? (
        <CircularProgress />
      ) : (
        <div className="dataGrid">
          <DataGrid
            rows={data.length ? data : []} // Check that rows are not empty
            columns={columns}
            pageSize={5}
            checkboxSelection
            disableSelectionOnClick
            onSelectionModelChange={(ids) =>
              setSelectedRow(ids.length ? ids[0] : null)
            }
          />
        </div>
      )}

      {/* Add Crop Dialog */}
      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <DialogTitle>Add New Crop</DialogTitle>
        <DialogContent>
          <TextField
            label="Crop Name"
            name="crop_name"
            value={formData.crop_name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Crop Stage"
            name="crop_stage"
            value={formData.crop_stage}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Starting Date"
            name="starting_date"
            type="date"
            value={formData.starting_date}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
          <TextField
            label="Ending Date"
            name="ending_date"
            type="date"
            value={formData.ending_date}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
          <TextField
            label="Humidity (%)"
            name="humidity_percentage"
            value={formData.humidity_percentage}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Temperature Range"
            name="temperature_range"
            value={formData.temperature_range}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sky"
            name="sky"
            value={formData.sky}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Disease"
            name="disease"
            value={formData.disease}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Pests"
            name="pests"
            value={formData.pests}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          {/* <TextField
            label="Products"
            name="products"
            value={formData.products}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          /> */}
          {/* <Autocomplete
            multiple
            id="products"
            options={productsList}
            getOptionLabel={(option) => option.name} // Assuming 'name' is the product name field
            value={formData.products}
            onChange={handleProductSelection}
            renderInput={(params) => <TextField {...params} label="Products" />}
            fullWidth
            margin="normal"
          /> */}
          <Autocomplete
            multiple
            id="products"
            options={productsList}
            getOptionLabel={(option) => option.product_name || ""}
            value={formData.products}
            onChange={handleProductSelection}
            renderInput={(params) => <TextField {...params} label="Products" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddModalOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddNew} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Crop Dialog */}
      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <DialogTitle>Edit Crop</DialogTitle>
        <DialogContent>
          <TextField
            label="Crop Name"
            name="crop_name"
            value={formData.crop_name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Crop Stage"
            name="crop_stage"
            value={formData.crop_stage}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Starting Date"
            name="starting_date"
            type="date"
            value={formData.starting_date}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Ending Date"
            name="ending_date"
            type="date"
            value={formData.ending_date}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Humidity (%)"
            name="humidity_percentage"
            value={formData.humidity_percentage}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Temperature Range"
            name="temperature_range"
            value={formData.temperature_range}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Sky"
            name="sky"
            value={formData.sky}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Disease"
            name="disease"
            value={formData.disease}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Pests"
            name="pests"
            value={formData.pests}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <Autocomplete
            multiple
            id="products"
            options={productsList}
            getOptionLabel={(option) => option.product_name || ""} // assuming 'product_name' is correct
            value={formData.products || []} // Ensure products is always an array
            onChange={handleProductSelection}
            renderInput={(params) => <TextField {...params} label="Products" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditModalOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {deleting ? (
            <CircularProgress />
          ) : (
            <>
              <p>
                Are you sure you want to delete this crop stage? This action
                cannot be undone.
              </p>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmationClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="secondary"
            variant="contained"
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Snackbar */}
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={closeToast}>
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={toast.severity}
          onClose={closeToast}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default CropStages;
